import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@core/models/common.model';
import { Organization, OrganizationDetail } from '@core/models/organization.model';

@Injectable({ providedIn: 'root' })
export class OrganizationContextService {
  private readonly base = environment.apiBaseUrl;
  private readonly _debugLogBaseUrl = console.log('OrganizationContextService base URL:', environment.apiBaseUrl);

  private readonly tenantIdSubject = new BehaviorSubject<string | null>(null);
  private readonly organizationSubject = new BehaviorSubject<Organization | null>(null);
  private readonly organizationDetailSubject = new BehaviorSubject<OrganizationDetail | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  readonly tenantId$ = this.tenantIdSubject.asObservable();
  readonly organization$ = this.organizationSubject.asObservable();
  readonly organizationDetail$ = this.organizationDetailSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  readonly isDigitalPrescriptionEnabled$: Observable<boolean> = this.organizationDetail$.pipe(
    map((detail) => detail?.enableDigitalPrescription ?? false),
  );

  constructor(private readonly http: HttpClient) {}

  get tenantId(): string | null {
    return this.tenantIdSubject.value;
  }

  get organization(): Organization | null {
    return this.organizationSubject.value;
  }

  get organizationDetail(): OrganizationDetail | null {
    return this.organizationDetailSubject.value;
  }

  setTenant(tenantId: string | null): void {
    if (!tenantId) {
      this.clear();
      return;
    }

    if (this.tenantIdSubject.value === tenantId && this.organizationSubject.value) {
      return;
    }

    this.tenantIdSubject.next(tenantId);
    this.refreshOrganization(tenantId);
  }

  refreshOrganization(tenantId?: string): void {
    const targetTenantId = tenantId ?? this.tenantIdSubject.value;
    if (!targetTenantId) {
      return;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    this.http
      .get<ApiResponse<Organization>>(`${this.base}/Organization/tenant/${targetTenantId}`)
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            this.errorSubject.next(response.title ?? response.message ?? 'Failed to fetch organization');
            this.loadingSubject.next(false);
            return;
          }

          this.organizationSubject.next(response.data);
          this.organizationDetailSubject.next(response.data.organizationDetail ?? null);
          this.loadingSubject.next(false);
        },
        error: (error: { error?: { title?: string; message?: string } }) => {
          this.errorSubject.next(error?.error?.title ?? error?.error?.message ?? 'Failed to fetch organization');
          this.loadingSubject.next(false);
        },
      });
  }

  updateOrganizationDetail<K extends keyof OrganizationDetail>(column: K, value: OrganizationDetail[K]): void {
    const current = this.organizationDetailSubject.value ?? {};
    this.organizationDetailSubject.next({
      ...current,
      [column]: value,
    });
  }

  clear(): void {
    this.tenantIdSubject.next(null);
    this.organizationSubject.next(null);
    this.organizationDetailSubject.next(null);
    this.loadingSubject.next(false);
    this.errorSubject.next(null);
  }
}

